import React from 'react';
import { Helmet } from 'react-helmet';

interface OrganizationSchemaProps {
  url: string;
  logo: string;
  name: string;
  description: string;
  address: {
    streetAddress: string;
    addressLocality: string;
    postalCode: string;
    addressCountry: string;
  };
  contactPoint: {
    telephone: string;
    email: string;
  };
  sameAs: string[];
}

interface WebsiteSchemaProps {
  url: string;
  name: string;
  description: string;
  inLanguage: string;
}

interface BreadcrumbSchemaProps {
  items: Array<{
    name: string;
    item: string;
  }>;
}

interface ProductSchemaProps {
  name: string;
  description: string;
  image: string;
  sku?: string;
  brand?: string;
  offers?: {
    price?: string;
    priceCurrency?: string;
    availability?: string;
  };
}

interface SchemaOrgProps {
  type: 'organization' | 'website' | 'breadcrumb' | 'product';
  data: OrganizationSchemaProps | WebsiteSchemaProps | BreadcrumbSchemaProps | ProductSchemaProps;
}

/**
 * Componente SchemaOrg para implementar el marcado estructurado Schema.org
 * Permite añadir diferentes tipos de esquemas: organización, sitio web, migas de pan, producto, etc.
 */
export default function SchemaOrg({ type, data }: SchemaOrgProps) {
  let schemaData = {};

  switch (type) {
    case 'organization':
      const orgData = data as OrganizationSchemaProps;
      schemaData = {
        '@context': 'https://schema.org',
        '@type': 'Organization',
        url: orgData.url,
        logo: orgData.logo,
        name: orgData.name,
        description: orgData.description,
        address: {
          '@type': 'PostalAddress',
          streetAddress: orgData.address.streetAddress,
          addressLocality: orgData.address.addressLocality,
          postalCode: orgData.address.postalCode,
          addressCountry: orgData.address.addressCountry,
        },
        contactPoint: {
          '@type': 'ContactPoint',
          telephone: orgData.contactPoint.telephone,
          email: orgData.contactPoint.email,
          contactType: 'customer service',
        },
        sameAs: orgData.sameAs,
      };
      break;

    case 'website':
      const websiteData = data as WebsiteSchemaProps;
      schemaData = {
        '@context': 'https://schema.org',
        '@type': 'WebSite',
        url: websiteData.url,
        name: websiteData.name,
        description: websiteData.description,
        inLanguage: websiteData.inLanguage,
      };
      break;

    case 'breadcrumb':
      const breadcrumbData = data as BreadcrumbSchemaProps;
      schemaData = {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbData.items.map((item, index) => ({
          '@type': 'ListItem',
          position: index + 1,
          name: item.name,
          item: item.item,
        })),
      };
      break;

    case 'product':
      const productData = data as ProductSchemaProps;
      schemaData = {
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: productData.name,
        description: productData.description,
        image: productData.image,
        ...(productData.sku && { sku: productData.sku }),
        ...(productData.brand && {
          brand: {
            '@type': 'Brand',
            name: productData.brand,
          }
        }),
        ...(productData.offers && {
          offers: {
            '@type': 'Offer',
            price: productData.offers.price,
            priceCurrency: productData.offers.priceCurrency || 'EUR',
            availability: productData.offers.availability || 'https://schema.org/InStock',
          }
        }),
      };
      break;

    default:
      break;
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(schemaData)}
      </script>
    </Helmet>
  );
}
